
import React, { useState, useEffect, useMemo } from 'react';
import { Search, MapPin, Loader2 } from 'lucide-react';
import { Booth } from './types';
import { fetchBoothData } from './services/dataService';
import BoothMap from './components/BoothMap';
import InfoDrawer from './components/InfoDrawer';

const App: React.FC = () => {
  const [allBooths, setAllBooths] = useState<Booth[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBooth, setSelectedBooth] = useState<Booth | null>(null);
  
  // Initialize favorites directly from localStorage to prevent race conditions
  const [favorites, setFavorites] = useState<Set<string>>(() => {
    const saved = localStorage.getItem('booth_favorites');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return new Set(parsed);
      } catch (e) {
        console.error('Failed to parse initial favorites', e);
      }
    }
    return new Set();
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await fetchBoothData();
        setAllBooths(data);
      } catch (err) {
        console.error('Failed to load booth data', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Save favorites to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('booth_favorites', JSON.stringify(Array.from(favorites)));
  }, [favorites]);

  const toggleFavorite = (boothId: string) => {
    setFavorites(prev => {
      const next = new Set(prev);
      if (next.has(boothId)) {
        next.delete(boothId);
      } else {
        next.add(boothId);
      }
      return next;
    });
  };

  // Filtering Logic
  const filteredBoothIds = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return new Set(allBooths.map(b => b.id));

    return new Set(
      allBooths
        .filter(b => 
          b.id.toLowerCase().includes(query) ||
          b.circleName.toLowerCase().includes(query) ||
          b.author.toLowerCase().includes(query) ||
          b.keywords.some(k => k.toLowerCase().includes(query)) ||
          b.tags.some(t => t.toLowerCase().includes(query))
        )
        .map(b => b.id)
    );
  }, [allBooths, searchQuery]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-white text-orange-500">
        <Loader2 className="animate-spin mb-4" size={48} />
        <p className="font-bold text-gray-600">載入地圖資料中...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen w-full bg-white relative overflow-hidden">
      {/* Header - Centered title, adjusted layout */}
      <header className="bg-white/95 backdrop-blur-md border-b border-gray-100 p-6 sticky top-0 z-30 w-full">
        <div className="max-w-4xl mx-auto flex flex-col items-center">
          {/* Title centered */}
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-black text-gray-900 flex items-center gap-3 mb-6">
            <MapPin className="text-orange-500" size={32} /> Furrymosa8 互動攤位地圖
          </h1>

          {/* Instruction text */}
          <div className="text-center mb-6 text-gray-500 font-bold text-sm sm:text-base animate-fade-in tracking-tight">
            <div>可縮放、拖移地圖・點擊攤位查看詳情・將攤位加入最愛標記位置</div>
            <div className="text-xs sm:text-sm font-medium mt-1 text-gray-400 flex items-center justify-center gap-1.5">
              <span className="w-2.5 h-2.5 bg-amber-300 rounded-sm shadow-sm"></span>
              <span>黃色深淺代表資料填寫完整度</span>
            </div>
          </div>
          
          <div className="w-full">
            <div className="relative mb-3">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input 
                type="text"
                placeholder="搜尋社團名、攤位編號、作者、關鍵字..."
                className="w-full pl-12 pr-4 py-4 bg-gray-100 border-none rounded-2xl focus:ring-2 focus:ring-orange-500 transition-all outline-none text-base font-medium shadow-inner"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            {/* Results count badge - moved below search, left aligned */}
            <div className="flex justify-start">
              <div className="text-xs font-bold text-orange-600 bg-orange-50 px-4 py-1.5 rounded-full whitespace-nowrap shadow-sm border border-orange-100">
                符合結果：{filteredBoothIds.size} 筆
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Map Container */}
      <main className="flex-1 relative overflow-hidden w-full">
        <BoothMap 
          booths={allBooths} 
          filteredBooths={filteredBoothIds} 
          favorites={favorites}
          onBoothClick={setSelectedBooth}
        />
      </main>

      {/* Details Drawer */}
      <InfoDrawer 
        booth={selectedBooth} 
        isFavorite={selectedBooth ? favorites.has(selectedBooth.id) : false}
        onToggleFavorite={toggleFavorite}
        onClose={() => setSelectedBooth(null)} 
      />

      {/* Footer Info */}
      <footer className="w-full p-4 bg-gray-50 text-sm sm:text-base text-gray-500 border-t">
        <div className="max-w-4xl mx-auto text-center font-bold tracking-tight">
          資料來源 © Furrymosa / <a href="https://script.google.com/macros/s/AKfycbxbic3yNq2TTm3l1xOOn4bn1dhs2BMze3Rpow5kUiMybwuLVt6pq7zvYkdWC902PCJr/exec" target="_blank" rel="noopener noreferrer" className="hover:text-orange-500 underline underline-offset-2">攤主資料登記表單</a>
        </div>
      </footer>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-5px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
      `}</style>
    </div>
  );
};

export default App;
